/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  editImage,
  generateImageFromText,
  generatePromptFromImage,
  generatePromptSequence,
  generateSequenceFromConversation,
  generateSequenceFromConversationStream,
  generateStoryboard,
  toggleTranslateText,
} from '../services/geminiService';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../services/supabaseClient';
import { compilePromptForProvider } from '../services/promptCompiler';
import { getVideoModels, getModelById, ModelDefinition } from '../services/modelRegistry';
import {
  AspectRatio,
  Character,
  ChatMessage,
  Dogma,
  GenerateVideoParams,
  GenerationMode,
  ImageFile,
  PromptSequence,
  Resolution,
  Storyboard,
  VeoModel,
  VideoFile,
  VideoProvider,
} from '../types';
import AIEditorModal from './AIEditorModal';
// [REMOVED] CameraKit import - feature removed
import {
  ArrowRightIcon,
  ChevronDownIcon,
  FilmIcon,
  FramesModeIcon,
  LayoutGridIcon,
  MagicWandIcon,
  PaperclipIcon,
  PencilIcon,
  RectangleStackIcon,
  ReferencesModeIcon,
  ScissorsIcon,
  SwapHorizontallyIcon,
  TextModeIcon,
  TvIcon,
  UsersIcon,
  VideoIcon,
  XMarkIcon,
  SlidersHorizontalIcon,
  UploadCloudIcon,
  SparklesIcon
} from './icons';
import { LTXParamsPanel, LTX_DEFAULT_PARAMS } from './LTXParamsPanel';
import { LTXParams } from '../types';
import { fileToBase64, ImageUpload } from './PromptForm';
import ImageGenerationModal from './ImageGenerationModal';
import StoryboardPreviewModal from './StoryboardPreviewModal';
import VideoAnalysisModal from './VideoAnalysisModal';
import VideoFrameSelectorModal from './VideoFrameSelectorModal';
import { base64ToFile } from '../utils/base64';
import { VideoSettings, EXPERIMENTAL_TRAINING, START_FRAME_MODE_OPTIONS, StartFrameMode } from '../types/videoSettings';
import { ImageModelProvider } from '../services/imageGenerationService';
import { connectDrive, isDriveConnected } from '../services/googleDriveClient';
import { DriveFilePicker } from './DriveFilePicker';

interface PromptSequenceAssistantProps {
  imageProvider: ImageModelProvider;
  onImageProviderChange: (provider: ImageModelProvider) => void;
  onSequenceGenerated: (sequence: PromptSequence, isExtension: boolean, masterShot?: ImageFile) => void;
  activeDogma: Dogma | null;
  onOpenDogmaManager: () => void;
  onOpenCharacterManager: () => void;
  extensionContext?: ImageFile | null;
  initialValues?: GenerateVideoParams | null;
  onGenerate: (params: GenerateVideoParams) => void;
  onStartExternalExtensionAssistant: (context: {
    lastFrame: ImageFile;
    motionDescription: string;
    originalVideo: File;
  }) => void;
  onClearContext: () => void;
  assistantImage: ImageFile | null;
  onAssistantImageChange: (image: ImageFile | null) => void;
  assistantReferenceVideo: VideoFile | null;
  videoForExtension?: any | null; // Changed from Video to any
  characterToInject: Character | null;
  onCharacterInjected: () => void;
  characters: Character[];
  onMentionedCharactersChange: (characters: Character[]) => void;
  displayMode?: 'chat' | 'context' | 'split'; // P1: UI Mode
  showContextPanel?: boolean; // P1: Toggle context panel
  motionDescription?: string | null; // Continuity context from modal
  onProvisionalSequence?: (prompt: string) => void; // P1: Auto-trigger keyframe
  apiKey?: string | null; // P0.6: BYOK Strict (Gemini)
  ltxApiKey?: string | null; // P0.6: BYOK Strict (LTX/HuggingFace)
  // P0: Lift messages state to parent for persistence
  messages?: ChatMessage[];
  onMessagesChange?: (messages: ChatMessage[]) => void;
  // Mode toggle: Plan Extensif / Découpage
  sequenceMode?: 'plan-sequence' | 'decoupage' | 'infinite-chain';
  onSequenceModeChange?: (mode: 'plan-sequence' | 'decoupage' | 'infinite-chain') => void;
  // Video Settings Update
  videoSettings?: VideoSettings;
  onVideoSettingsChange?: (settings: VideoSettings) => void;
  /** Whether a reference image (keyframe/storyboard) is available for start frame mode */
  hasReferenceImage?: boolean;
  // Studio Visibility Toggle
  forceConfigMode?: boolean;
  onToggleConfigMode?: () => void;
  projectName?: string;
  // Multi-task: notify parent of loading state
  onBusyChange?: (busy: boolean) => void;
  // Decoupage: Generated shots callback
  onDecoupageGenerated?: (shots: any[]) => void;
}



type AssistantResult = {
  creativePrompt: string;
  veoOptimizedPrompt: string;
};

// === PRE-BRIEF TYPES ===
type AssistantPhase = 'input' | 'prebrief' | 'proposal' | 'json';

interface PreBriefData {
  subject: string;
  location: string;
  timeMood: string;
  camera: string;
  intent: string;
}

const PREBRIEF_DEFAULTS: PreBriefData = {
  subject: '',
  location: 'Studio / Neutral',
  timeMood: 'Professional Lighting',
  camera: 'Medium Shot, Stable',
  intent: 'Clear & Informative'
};

const PREBRIEF_OPTIONS = {
  location: ['Studio', 'Office', 'Outdoors', 'Modern Home', 'Abstract', 'Cyberpunk City', 'Nature'],
  timeMood: ['Daylight', 'Golden Hour', 'Night', 'Cinematic', 'Moody', 'Bright & Airy', 'Tech Blue'],
  camera: ['Wide Angle', 'Medium Shot', 'Close-up', 'Handheld', 'Drone', 'Static Tripod'],
  intent: ['Advertising', 'Storytelling', 'Tutorial', 'Emotional', 'Action-packed', 'Slow Paced']
};

interface ProposalPack {
  id: string;
  title: string;
  description: string;
  reasoning: string;
}

interface ChainStep {
  id: string;
  startImage: ImageFile | null;
  prompt: string;
  endImage: ImageFile | null;
  isGenerating: boolean;
}

const aspectRatioDisplayNames: Record<AspectRatio, string> = {
  [AspectRatio.LANDSCAPE]: 'Landscape (16:9)',
  [AspectRatio.PORTRAIT]: 'Portrait (9:16)',
};

const qualityDisplayNames: Record<Resolution, string> = {
  [Resolution.P720]: 'Standard (720p)',
  [Resolution.P1080]: 'HD (1080p)',
};

// modelDisplayNames deprecated in favor of registry labels

const modeIcons: Record<GenerationMode, React.ReactNode> = {
  [GenerationMode.TEXT_TO_VIDEO]: <TextModeIcon className="w-5 h-5" />,
  [GenerationMode.FRAMES_TO_VIDEO]: <FramesModeIcon className="w-5 h-5" />,
  [GenerationMode.REFERENCES_TO_VIDEO]: (
    <ReferencesModeIcon className="w-5 h-5" />
  ),
  [GenerationMode.EXTEND_VIDEO]: <FilmIcon className="w-5 h-5" />,
};

const fileToImageFile = (file: File): Promise<ImageFile> =>
  fileToBase64<ImageFile>(file);



const CustomSelect: React.FC<{
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  icon: React.ReactNode;
  children: React.ReactNode;
  disabled?: boolean;
}> = ({ label, value, onChange, icon, children, disabled = false }) => (
  <div>
    <label
      className={`text-xs block mb-1.5 font-medium ${disabled ? 'text-gray-500' : 'text-gray-400'
        }`}>
      {label}
    </label>
    <div className="relative">
      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
        {icon}
      </div>
      <select
        value={value}
        onChange={onChange}
        disabled={disabled}
        className="w-full bg-[#1f1f1f] border border-gray-600 rounded-lg pl-10 pr-8 py-2.5 appearance-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-700/50 disabled:border-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed">
        {children}
      </select>
      <ChevronDownIcon
        className={`w-5 h-5 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none ${disabled ? 'text-gray-600' : 'text-gray-400'
          }`}
      />
    </div>
  </div>
);

const PromptSequenceAssistant: React.FC<PromptSequenceAssistantProps> = ({
  imageProvider,
  onImageProviderChange,
  onSequenceGenerated,
  activeDogma,
  onOpenDogmaManager,
  onOpenCharacterManager,
  extensionContext,
  initialValues,
  onGenerate,
  onStartExternalExtensionAssistant,
  onClearContext,
  assistantImage,
  onAssistantImageChange,
  assistantReferenceVideo,
  videoForExtension,
  characterToInject,
  onCharacterInjected,
  characters,
  onMentionedCharactersChange,
  motionDescription,
  onProvisionalSequence,
  apiKey,
  ltxApiKey,
  // P0: Message persistence props
  messages: messagesProp,
  onMessagesChange,
  // Mode toggle props
  sequenceMode = 'plan-sequence',
  onSequenceModeChange,
  forceConfigMode = false,
  onToggleConfigMode,
  projectName,
  onBusyChange,
  onDecoupageGenerated,
  videoSettings,
  onVideoSettingsChange,
  hasReferenceImage = false,
}) => {
  const { user } = useAuth();

  // Auth readiness gate - buttons should be disabled until ready
  const isAuthReady = !!user;
  // Helper to map new engine ID back to legacy VideoProvider enum for services
  // FIX: Add null guard to prevent crash on first render when videoSettings may be undefined
  const currentVideoProvider = React.useMemo(() => {
    if (!videoSettings) return VideoProvider.VERTEX;
    if (videoSettings.engine === 'veo') return VideoProvider.VERTEX;
    if (videoSettings.engine === 'ltx2_fal') return VideoProvider.LTX;
    return VideoProvider.VERTEX;
  }, [videoSettings?.engine]);
  // --- Merged State from PromptForm ---
  const [prompt, setPrompt] = useState(initialValues?.prompt ?? '');

  // Model Selection (Dynamic from Registry)
  const videoModels = getVideoModels();
  const [selectedModelId, setSelectedModelId] = useState<string>(
    initialValues?.model ? (initialValues.model as string) : (videoModels[0]?.id || 'veo')
  );

  const [aspectRatio, setAspectRatio] = useState<AspectRatio>(
    initialValues?.aspectRatio ?? AspectRatio.LANDSCAPE,
  );

  const [resolution, setResolution] = useState<Resolution>(
    initialValues?.resolution ?? Resolution.P720,
  );
  const [generationMode, setGenerationMode] = useState<GenerationMode>(
    initialValues?.mode ?? GenerationMode.TEXT_TO_VIDEO,
  );
  const [startFrame, setStartFrame] = useState<ImageFile | null>(
    initialValues?.startFrame ?? null,
  );
  const [endFrame, setEndFrame] = useState<ImageFile | null>(
    initialValues?.endFrame ?? null,
  );
  const [referenceImages, setReferenceImages] = useState<ImageFile[]>(
    initialValues?.referenceImages ?? [],
  );
  const [inputVideoObject, setInputVideoObject] = useState<any | null>(
    initialValues?.inputVideoObject ?? null,
  );
  const [isLooping, setIsLooping] = useState(
    initialValues?.isLooping ?? false,
  );
  // [REMOVED] activeTab state - Studio tab removed, single unified interface
  // [REMOVED] isCameraKitOpen state - CameraKit removed
  const [isModeSelectorOpen, setIsModeSelectorOpen] = useState(false);
  const [isAutoDescribing, setIsAutoDescribing] = useState(false);
  const [videoForAnalysis, setVideoForAnalysis] = useState<File | null>(null);
  const [editingImage, setEditingImage] = useState<{
    image: ImageFile;
    onConfirm: (newImage: ImageFile) => void;
  } | null>(null);
  const [storyboard, setStoryboard] = useState<Storyboard | null>(null);
  const [isGeneratingStoryboard, setIsGeneratingStoryboard] = useState(false);
  const [storyboardError, setStoryboardError] = useState<string | null>(null);
  const [isFrameSelectorOpen, setIsFrameSelectorOpen] = useState<{
    onConfirm: (img: ImageFile) => void;
  } | null>(null);
  const [imageGenerationTarget, setImageGenerationTarget] = useState<
    'start' | 'end' | null
  >(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [isBananaOpen, setIsBananaOpen] = useState(false);
  const [isDrivePickerOpen, setIsDrivePickerOpen] = useState(false);

  // Connection Handlers
  const handleGoogleDriveClick = async () => {
    const connected = await isDriveConnected();
    if (!connected) {
      await connectDrive();
      return;
    }
    setIsDrivePickerOpen(true);
  };

  const handleDriveFileSelect = (file: any) => {
    if (onVideoSettingsChange && videoSettings) {
      onVideoSettingsChange({
        ...videoSettings,
        externalVideo: {
          source: 'gdrive',
          fileName: file.name,
          url: file.id,
        }
      });
    }
    setIsDrivePickerOpen(false);
  };
  // [REMOVED] videoProvider state - moved to Studio VideoSettings
  // [REMOVED] withAudio state - moved to Studio VideoSettings
  const [withAudio, setWithAudio] = useState(
    initialValues?.withAudio ?? true
  );

  // --- Assistant-specific State ---
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [userInput, setUserInput] = useState('');
  const [duration, setDuration] = useState('8');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [finalResult, setFinalResult] = useState<AssistantResult | null>(null);
  const [isConfiguring, setIsConfiguring] = useState(false);

  // === PRE-BRIEF STATE ===
  const [assistantPhase, setAssistantPhase] = useState<AssistantPhase>('input');
  const [preBriefData, setPreBriefData] = useState<PreBriefData>(PREBRIEF_DEFAULTS);
  const [proposals, setProposals] = useState<ProposalPack[]>([]);
  const [selectedProposalId, setSelectedProposalId] = useState<string | null>(null);
  const [hypothesis, setHypothesis] = useState<string>('');
  const [isGeneratingProposals, setIsGeneratingProposals] = useState(false);

  // === INFINITE CHAIN STATE ===
  const [chainSteps, setChainSteps] = useState<ChainStep[]>([
    { id: '1', startImage: null, prompt: '', endImage: null, isGenerating: false }
  ]);
  const chainEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (sequenceMode === 'infinite-chain' && chainEndRef.current) {
      chainEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chainSteps.length, sequenceMode]);

  // === MULTI-TASK: Notify parent of busy state ===
  useEffect(() => {
    onBusyChange?.(isLoading);
  }, [isLoading, onBusyChange]);


  // LTX Params
  const [ltxParams, setLtxParams] = useState<LTXParams>(LTX_DEFAULT_PARAMS);
  const [isLtxSettingsOpen, setIsLtxSettingsOpen] = useState(false);


  const [referenceVideoUrl, setReferenceVideoUrl] = useState<string | null>(
    null,
  );

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const modeSelectorRef = useRef<HTMLDivElement>(null);
  const externalVideoInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const chatTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(-1);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');

  const resetAssistantState = useCallback(
    (
      context?: {
        lastFrame: ImageFile;
        motionDescription: string;
      } | 'new_chat',
    ) => {
      let initialMessage: ChatMessage;
      if (context && context !== 'new_chat') {
        initialMessage = {
          role: 'assistant',
          content: `Je suis prêt à vous aider à étendre votre vidéo. En me basant sur cette dernière image, que doit-il se passer ensuite pour continuer l'action de manière fluide ? J'ai bien noté le vecteur de mouvement précédent, affiché à droite.`,
          image: context.lastFrame,
        };
        setDuration('4');
      } else {
        initialMessage = {
          role: 'assistant',
          content:
            "Hello! I'm your sequence planning assistant. Describe the scene you'd like to create, and we'll build the prompts together. What's your idea?",
          image: null,
        };
        setDuration('8');
      }
      setMessages([initialMessage]);
      setFinalResult(null);
      setUserInput('');
      onAssistantImageChange(null);
      setError(null);
      setIsLoading(false);
      setIsConfiguring(false);
    },
    [onAssistantImageChange],
  );

  useEffect(() => {
    if (initialValues) {
      setPrompt(initialValues.prompt ?? '');
      // [REMOVED] model selection - controlled by Studio
      setAspectRatio(initialValues.aspectRatio ?? AspectRatio.LANDSCAPE);
      setResolution(initialValues.resolution ?? Resolution.P720);
      setGenerationMode(initialValues.mode ?? GenerationMode.TEXT_TO_VIDEO);
      setStartFrame(initialValues.startFrame ?? null);
      setEndFrame(initialValues.endFrame ?? null);
      setReferenceImages(initialValues.referenceImages ?? []);
      setInputVideoObject(initialValues.inputVideoObject ?? null);
      setIsLooping(initialValues.isLooping ?? false);
      setIsConfiguring(true);
      // [REMOVED] setActiveTab - Studio tab removed
      setMessages([]);
    } else if (characterToInject) {
      resetAssistantState('new_chat');
      const initialMessage: ChatMessage = {
        role: 'assistant',
        content: `J'ai chargé le personnage "${characterToInject.name}". Construisons une scène pour lui/elle. Que doit-il se passer ?`,
        image: null,
      };
      setMessages([initialMessage]);
      setUserInput(
        `@${characterToInject.name}, ${characterToInject.description}`,
      );
      onCharacterInjected();
    } else if (extensionContext) {
      const simpleExtensionContext = {
        lastFrame: extensionContext,
        motionDescription: "l'action précédente",
      };
      resetAssistantState(simpleExtensionContext);
    } else {
      resetAssistantState('new_chat');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues, extensionContext, characterToInject]);

  // Effect to Sync Start Frame to Assistant Context
  useEffect(() => {
    // Always sync since we no longer have tabs - Assistant is always active
    if (startFrame && !assistantImage && !extensionContext) {
      onAssistantImageChange(startFrame);
    }
  }, [startFrame, assistantImage, onAssistantImageChange, extensionContext]);


  useEffect(() => {
    const mentionRegex = /@([a-zA-Z0-9_]+)/g;
    const matches = userInput.match(mentionRegex);
    if (matches) {
      const mentionedNames = matches.map((mention) =>
        mention.substring(1).toLowerCase(),
      );
      const foundCharacters = characters.filter((char) =>
        mentionedNames.includes(char.name.toLowerCase()),
      );
      onMentionedCharactersChange(
        Array.from(new Map(foundCharacters.map((c) => [c.id, c])).values()),
      );
    } else {
      onMentionedCharactersChange([]);
    }
  }, [userInput, characters, onMentionedCharactersChange]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, finalResult]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [prompt]);

  useEffect(() => {
    const textarea = chatTextareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [userInput]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modeSelectorRef.current &&
        !modeSelectorRef.current.contains(event.target as Node)
      ) {
        setIsModeSelectorOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    let objectUrl: string | null = null;
    if (assistantReferenceVideo) {
      objectUrl = URL.createObjectURL(assistantReferenceVideo.file);
      setReferenceVideoUrl(objectUrl);
    } else {
      setReferenceVideoUrl(null);
    }
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [assistantReferenceVideo]);

  const normalize = useCallback((str: string) =>
    str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase(), []);

  const detectedChars = React.useMemo(() => {
    if (!characters || characters.length === 0) return [];
    const normalizedInput = normalize(userInput);
    return characters.filter(c => {
      const normalizedName = normalize(c.name);
      const nameEscaped = normalizedName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(@${nameEscaped}\\b|\\b${nameEscaped}\\b)`, 'i');
      return regex.test(normalizedInput);
    });
  }, [characters, userInput, normalize]);

  const filteredCharacters = React.useMemo(() => {
    if (!mentionFilter) return characters;
    const query = normalize(mentionFilter);
    return characters.filter(c => normalize(c.name).includes(query));
  }, [characters, mentionFilter, normalize]);

  const handleInsertMention = (char: Character) => {
    const val = userInput;
    const lastAtIndex = val.lastIndexOf('@');
    const before = val.substring(0, lastAtIndex);
    const after = val.substring(lastAtIndex + 1).split(/\s/).slice(1).join(' '); // Keep text after the filter word
    const space = ' ';
    setUserInput(before + '@' + char.name + space + after);
    setShowMentions(false);
    chatTextareaRef.current?.focus();
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const imageFile = await fileToImageFile(file);
        onAssistantImageChange(imageFile);
      } catch (error) {
        console.error('Error handling image upload:', error);
        setError('Failed to process the image file.');
      }
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleExternalVideoFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Basic size check (approx 200MB)
      if (file.size > 200 * 1024 * 1024) {
        setError("Video file is too large (Maximum 200MB).");
        return;
      }
      setInputVideoObject(file);
      // Automatically switch to Extension mode to show context
      handleSelectMode(GenerationMode.EXTEND_VIDEO);
    }
    if (externalVideoInputRef.current) {
      externalVideoInputRef.current.value = '';
    }
  };

  const [pendingDecoupagePrompt, setPendingDecoupagePrompt] = useState<string | null>(null);
  const [pendingDecoupageShots, setPendingDecoupageShots] = useState<any[] | null>(null);

  const handleChatSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAuthReady) {
      setError('Veuillez vous connecter pour utiliser l\'assistant.');
      return;
    }
    if (!userInput.trim() && !assistantImage) return;

    const currentMessages: ChatMessage[] = [
      ...messages,
      { role: 'user', content: userInput, image: assistantImage },
    ];
    setMessages(currentMessages);
    setUserInput('');

    // === PHASE 1: INPUT -> PRE-BRIEF ===
    // Instead of generating immediately, we move to Pre-brief
    if (assistantPhase === 'input') {
      setPreBriefData(prev => ({ ...prev, subject: userInput }));
      setAssistantPhase('prebrief');
      // Add user input to messages for history, but don't send to API yet
      setMessages(prev => [...prev, { role: 'user', content: userInput, image: assistantImage }]);
      return;
    }

    // === PHASE 2/3: EXECUTE GENERATION (JSON) ===
    executeGeneration(userInput);
  };

  /**
   * Generate creative proposals before committing to JSON generation
   * Uses AI to suggest 3 different approaches based on the pre-brief
   */
  const generateProposals = async () => {
    if (!preBriefData.subject.trim()) {
      setError('Veuillez entrer un sujet avant de générer des propositions.');
      return;
    }

    setIsGeneratingProposals(true);
    setError(null);
    setProposals([]);

    try {
      const preBriefContext = `
        Subject: ${preBriefData.subject}
        Location: ${preBriefData.location}
        Mood/Time: ${preBriefData.timeMood}
        Camera Style: ${preBriefData.camera}
        Intent: ${preBriefData.intent}
        ${hypothesis ? `Strategy: ${hypothesis}` : ''}
      `;

      const proposalPrompt = `
        Based on this creative brief, generate exactly 3 different creative approaches for a video sequence.
        Each approach should have a distinct artistic vision.

        BRIEF:
        ${preBriefContext}

        Respond in valid JSON format ONLY (no markdown):
        {
          "proposals": [
            {
              "id": "approach_1",
              "title": "Short title (3-5 words)",
              "description": "2-3 sentences describing the visual approach",
              "reasoning": "Why this approach works for the intent"
            }
          ]
        }

        Make proposals diverse:
        1. One classic/traditional approach
        2. One bold/experimental approach
        3. One hybrid/balanced approach
      `;

      // Use streaming chat to generate proposals
      const response = await fetch('/api/generate-content', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey || '',
        },
        body: JSON.stringify({
          model: 'gemini-2.5-flash-latest',
          contents: { parts: [{ text: proposalPrompt }] }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate proposals');
      }

      const data = await response.json();
      const content = data.candidates?.[0]?.content?.parts?.[0]?.text || '';

      // Parse JSON from response
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        if (parsed.proposals && Array.isArray(parsed.proposals)) {
          setProposals(parsed.proposals);
          setAssistantPhase('proposal');
        } else {
          throw new Error('Invalid proposal format');
        }
      } else {
        throw new Error('No valid JSON in response');
      }

    } catch (err: any) {
      console.error('[Proposals] Error:', err);
      setError(err.message || 'Échec de la génération des propositions');
    } finally {
      setIsGeneratingProposals(false);
    }
  };

  const executeGeneration = async (currentInput: string = '') => {
    setIsLoading(true);
    setError(null);

    // Construct System Context from Pre-brief
    const preBriefContext = `
    STRICT CONSTRAINTS FROM PRE-BRIEF:
    - Subject: ${preBriefData.subject}
    - Location: ${preBriefData.location}
    - Mood/Time: ${preBriefData.timeMood}
    - Camera Style: ${preBriefData.camera}
    - Intent: ${preBriefData.intent}
    
    HYPOTHESIS/STRATEGY:
    ${hypothesis}
    
    Apply these explicitly to the generated shots.
    `;

    const currentMessages: ChatMessage[] = [
      ...messages,
      // We don't add user input here if it was already added in handleChatSubmit
      // But if called from Proposal or Prebrief buttons, we might need a trigger message
      { role: 'user', content: `Generate the JSON sequence now. ${preBriefContext}`, image: assistantImage }
    ];
    // Don't duplicate if message was already added in phase 1? 
    // Actually handleChatSubmit adds it. If we are in 'prebrief', handleChatSubmit isn't called for the "Generate" button.
    // So we need to manage messages carefully.

    // Let's rely on 'messages' state being up to date, and just append the system instruction invisibly OR
    // Just pass it as additional context to the service.


    try {
      let additionalContext = '';
      if (characters && characters.length > 0) {
        const charList = characters.map(c => `- Name: "${c.name}", Description: ${c.description || 'No description'}`).join('\n');
        additionalContext = `AVAILABLE CHARACTERS (The user may mention them):\n${charList}`;
      }

      const relevantCharacters = characters.filter(c => {
        const normalizedName = normalize(c.name);
        const nameEscaped = normalizedName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(@${nameEscaped}\\b|\\b${nameEscaped}\\b)`, 'i');
        return regex.test(normalize(currentMessages.map(m => m.content || '').join('\n')));
      });

      // Add streaming response placeholder
      const streamingMessageIndex = currentMessages.length;
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: '', image: null },
      ]);

      // Streaming callback - progressively update message
      const onChunk = (chunk: string) => {
        setMessages((prev) => {
          const updated = [...prev];
          if (updated[streamingMessageIndex]) {
            updated[streamingMessageIndex] = {
              ...updated[streamingMessageIndex],
              content: (updated[streamingMessageIndex].content || '') + chunk,
            };
          }
          return updated;
        });
      };

      const result = await generateSequenceFromConversationStream(
        currentMessages,
        activeDogma,
        parseInt(duration, 10),
        onChunk,
        extensionContext,
        motionDescription,
        apiKey || undefined,
        sequenceMode,
        additionalContext,
        relevantCharacters,
        currentVideoProvider
      );

      // Handle final result (streaming already displayed the text)
      if (typeof result !== 'string' && result.creativePrompt && result.veoOptimizedPrompt) {
        setFinalResult(result as AssistantResult);

        // === INTERCEPTION FOR DECOUPAGE MODE ===
        if (sequenceMode === 'decoupage') {
          console.log('[Assistant] Decoupage mode detected. Parsing ShotSpec JSON...');

          let resultText = typeof result === 'string' ? result : ((result as any).rawText || result.creativePrompt || '');
          try {
            // Remove potential markdown code blocks
            const jsonStr = resultText.replace(/```json\n?|\n?```/g, '').trim();
            const shots = JSON.parse(jsonStr);

            if (Array.isArray(shots)) {
              // IMMEDIATE TRANSFER: Send shots to ShotsTable right away
              // Don't wait for Master Shot confirmation - this was causing disconnection
              console.log('[Assistant] Découpage: Importing', shots.length, 'shots directly to table');
              onDecoupageGenerated?.(shots);

              // Store for optional Master Shot enhancement
              setPendingDecoupageShots(shots);
              setPendingDecoupagePrompt((result as any).veoOptimizedPrompt || (result as any).creativePrompt);

              setMessages((prev) => [
                ...prev,
                { role: 'assistant', content: `✅ ${shots.length} plans importés vers Découpage Pro!\n\nVous pouvez maintenant cliquer sur un plan pour générer ses vignettes.\n\n💡 Optionnel: Générer un "Master Shot" pour enrichir le contexte visuel.`, image: null },
              ]);

              // Optional: Still offer Master Shot UI, but don't block the flow
              // setIsBananaOpen(true);
              setIsLoading(false);
              return;

            } else {
              console.warn("Decoupage result is not an array:", shots);
              throw new Error("Le résultat n'est pas une liste de plans valide.");
            }
          } catch (e) {
            console.error("Failed to parse decoupage result:", e);
            // Fallback to text display if parsing failed
            setMessages((prev) => [
              ...prev,
              { role: 'assistant', content: typeof result === 'string' ? result : JSON.stringify(result), image: null },
              { role: 'assistant', content: `⚠️ Erreur de parsing JSON. Assurez-vous que Gemini a retourné un tableau de shots valide.`, image: null },
            ]);
            setIsLoading(false);
            return;
          }
        }

        // === STANDARD FLOW (Plan Sequence) ===
        const durationNum = parseInt(duration, 10);
        const isExtension = !!extensionContext;

        try {
          let sequence;
          const isLTX = currentVideoProvider === VideoProvider.LTX;
          const mainLimit = isLTX ? 20 : 8;

          if (durationNum > mainLimit) {
            sequence = await generatePromptSequence(
              result.veoOptimizedPrompt,
              durationNum,
              activeDogma,
              null, // No master shot for standard flow
              apiKey || undefined,
              currentVideoProvider
            );
          } else {
            sequence = {
              id: crypto.randomUUID(),
              mainPrompt: result.veoOptimizedPrompt,
              extensionPrompts: [],
              status: 'clean' as any,
              dirtyExtensions: [],
            };
          }
          onSequenceGenerated(sequence, isExtension);
        } catch (seqErr) {
          console.error('[Assistant] Failed to generate sequence:', seqErr);
          setError(seqErr instanceof Error ? seqErr.message : 'Failed to generate sequence.');
        }
      }
    } catch (err) {
      const errorObj = err as any;
      const errorCode = errorObj.code || 'UNKNOWN_ERROR';
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';

      const isAuthError = errorMessage?.includes('401') || errorObj.status === 401 || errorMessage?.includes('API_KEY');
      if (isAuthError) {
        const authMsg = "⚠️ Échec : Clé API manquante ou invalide.\nVeuillez configurer votre clé Gemini dans les paramètres.";
        alert(authMsg);
        setError(authMsg);
      } else {
        console.error('[Assistant] Chat error', { code: errorCode, message: errorMessage });
        setError(errorMessage);
      }

      let displayMessage = errorMessage;
      if (errorCode === 'PAYLOAD_TOO_LARGE') {
        displayMessage = `⚠️ ${errorMessage}\n\n💡 Conseil : Supprimez les images du contexte ou commencez une nouvelle conversation.`;
      }
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: displayMessage, image: null },
      ]);
    } finally {
      if (sequenceMode !== 'decoupage') {
        setIsLoading(false);
      }
    }
  };

  // Called when user confirms the Nano-generated Master Shot
  const handleMasterShotConfirmed = async (masterShot: ImageFile) => {
    setIsBananaOpen(false);
    if (!finalResult || !pendingDecoupagePrompt) return;

    console.log('[Assistant] Master Shot confirmed. Generating Breakdown sequence...');
    setIsLoading(true);

    try {

      const durationNum = parseInt(duration, 10);

      // === DECOUPAGE MODE: PROCEED WITH PARSED SHOTS ===
      if (sequenceMode === 'decoupage' && pendingDecoupageShots) {
        console.log('[Assistant] Master Shot confirmed for Decoupage. Injecting context...');

        // Inject master shot visual context into the sequence
        // We can't modify the generic 'any[]' easily without knowing structure, 
        // but we can pass the master shot to the parent via valid means or assume onDecoupageGenerated handles it.
        // For now, we'll assume the parent (Studio) receives the shots and we just notify.
        // OR better: We inject the Master Shot as a 'referenceImage' property on the shots if possible.

        const enrichedShots = pendingDecoupageShots.map(shot => ({
          ...shot,
          masterShotBase64: masterShot.base64 // Custom prop for downstream components
        }));

        onDecoupageGenerated?.(enrichedShots);
        // Also pass the master shot explicitly via callback if possible, but the signature is shots[].
        // The onSequenceGenerated uses (sequence, isExt, masterShot). 
        // onDecoupageGenerated currently matches (shots: any[]).

        // Clear pending
        setPendingDecoupageShots(null);
        setPendingDecoupagePrompt(null);
        setIsLoading(false);
        return;
      }
      const sequence = await generatePromptSequence(
        finalResult.veoOptimizedPrompt,
        durationNum,
        activeDogma,
        masterShot, // Pass Master Shot context
        apiKey || undefined,
        currentVideoProvider
      );

      console.log('[Assistant] Breakdown Sequence generated with Master Shot context.');
      onSequenceGenerated(sequence, false, masterShot);
      setPendingDecoupagePrompt(null);
    } catch (err: any) {
      console.error('[Assistant] Failed to generate breakdown sequence:', err);

      const isAuthError = err.message?.includes('401') || err.status === 401 || err.message?.includes('API_KEY');
      if (isAuthError) {
        alert("⚠️ Échec : Clé API manquante ou invalide.\nVeuillez configurer votre clé Gemini dans les paramètres.");
      }
      setError('Failed to generate breakdown sequence.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFinalSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      // ... existing FinalSubmit logic (unchanged essentially, just verifying refs) ...
      // For brevity in this replacement, we assume standard generation button flow is separate
      // from the Chat flow. This function handles the "Generate Video" button in the form.

      const normalize = (str: string) => str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

      const foundCharacters: Character[] = [];
      if (characters && characters.length > 0) {
        const normalizedPrompt = normalize(prompt);
        characters.forEach(c => {
          if (new RegExp(`@${normalize(c.name).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i').test(normalizedPrompt)) {
            foundCharacters.push(c);
          }
        });
      }

      const characterImagesFromPrompt: ImageFile[] = foundCharacters.flatMap(
        (char) => char.images.map(img => ({ file: base64ToFile(img.base64, img.name, img.type), base64: img.base64 }))
      );

      const distinctImages = new Map<string, ImageFile>();
      // Add images from chat detection (if any still relevant)
      const chatImages: ImageFile[] = detectedChars.flatMap(
        (char) => char.images.map(img => ({ file: base64ToFile(img.base64, img.name, img.type), base64: img.base64 }))
      );

      [...chatImages, ...characterImagesFromPrompt].forEach(img => {
        distinctImages.set(img.file.name + img.base64.length, img);
      });

      const finalCharacterImages = Array.from(distinctImages.values());

      const compiledPrompt = compilePromptForProvider(
        prompt,
        { characters: characters, dogma: activeDogma },
        currentVideoProvider
      );

      const params: GenerateVideoParams = {
        prompt: compiledPrompt,
        provider: currentVideoProvider,
        model: currentVideoProvider === VideoProvider.LTX ? 'ltx-video-2b-v0.9.1' : (selectedModelId || 'veo'),
        duration: parseInt(duration),
        aspectRatio,
        resolution,
        mode: finalCharacterImages.length > 0 && generationMode === GenerationMode.TEXT_TO_VIDEO
          ? GenerationMode.REFERENCES_TO_VIDEO
          : generationMode,
        startFrame: assistantImage || undefined,
        endFrame: endFrame || undefined,
        referenceImages: [...referenceImages, ...finalCharacterImages],
        styleImage: null,
        inputVideo: inputVideoObject || undefined,
        inputVideoObject:
          generationMode === GenerationMode.EXTEND_VIDEO
            ? videoForExtension ?? inputVideoObject
            : null,
        isLooping,

        vertexConfig: currentVideoProvider === VideoProvider.VERTEX ? {
          projectId: localStorage.getItem('vertex_project_id') || '',
          location: localStorage.getItem('vertex_location') || '',
          accessToken: localStorage.getItem('vertex_token') || ''
        } : undefined,
        withAudio,
        ltxParams: currentVideoProvider === VideoProvider.LTX ? ltxParams : undefined,
      };

      onGenerate(params);
    },
    [
      prompt,
      selectedModelId,
      aspectRatio,
      resolution,
      generationMode,
      startFrame,
      endFrame,
      referenceImages,
      inputVideoObject,
      isLooping,
      onGenerate,
      videoForExtension,
      characters,
      normalize,
      detectedChars,
      currentVideoProvider, // Added currentVideoProvider to dependencies
      assistantImage, // Added assistantImage to dependencies
      ltxParams, // Added ltxParams to dependencies
      duration, // Added duration to dependencies
      activeDogma, // Added activeDogma to dependencies
      withAudio, // Added withAudio to dependencies
    ],
  );

  const handleAcceptFinalPrompt = async () => {
    if (!finalResult) return;
    setIsLoading(true);
    setError(null);
    try {
      const mentionRegex = /@([a-zA-Z0-9_]+)/g;
      const matches = finalResult.veoOptimizedPrompt.match(mentionRegex);
      const finalMentionedCharacters = matches
        ? characters.filter((char) =>
          matches
            .map((m) => m.substring(1).toLowerCase())
            .includes(char.name.toLowerCase()),
        )
        : [];

      const durationNum = parseInt(duration, 10);
      const isExtension = !!extensionContext;

      if (durationNum > 8) {
        // Determine Video Provider from Registry Selection
        const modelDef = getModelById(selectedModelId);
        let videoProvider: VideoProvider = VideoProvider.GEMINI;
        if (modelDef?.id.includes('ltx') || modelDef?.provider === 'fal') videoProvider = VideoProvider.LTX;

        const sequence = await generatePromptSequence(
          finalResult.veoOptimizedPrompt,
          durationNum,
          activeDogma,
          null, // No master shot
          apiKey || undefined,
          videoProvider
        );
        onSequenceGenerated(sequence, isExtension);
      } else {
        setPrompt(finalResult.veoOptimizedPrompt);
        if (isExtension) {
          setGenerationMode(GenerationMode.EXTEND_VIDEO);
          setInputVideoObject(videoForExtension ?? null);
        } else if (finalMentionedCharacters.length > 0) {
          setGenerationMode(GenerationMode.REFERENCES_TO_VIDEO);
        } else {
          const imageContext = assistantImage || messages.slice().reverse().find((m) => m.role === 'user' && m.image)?.image;
          setGenerationMode(imageContext ? GenerationMode.FRAMES_TO_VIDEO : GenerationMode.TEXT_TO_VIDEO);
          setStartFrame(imageContext || null);
        }
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'An unknown error occurred.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUploadAndDescribe = useCallback(
    async (
      imageFile: ImageFile,
      imageSetter: (img: ImageFile | null) => void,
      isReference = false,
    ) => {
      imageSetter(imageFile);
      const shouldDescribe = !prompt.trim() && !isReference;
      if (shouldDescribe) {
        setIsAutoDescribing(true);
        try {
          const description = await generatePromptFromImage(imageFile);
          setPrompt(description);
        } catch (error) {
          console.error('Auto-description failed:', error);
        } finally {
          setIsAutoDescribing(false);
        }
      }
    },
    [prompt],
  );

  const handleSelectMode = (mode: GenerationMode) => {
    setGenerationMode(mode);
    setIsModeSelectorOpen(false);
    setStartFrame(null);
    setEndFrame(null);
    setReferenceImages([]);
    setInputVideoObject(null);
    setIsLooping(false);
  };



  const handleAnalysisConfirm = (context: {
    lastFrame: ImageFile;
    motionDescription: string;
    originalVideo: File;
  }) => {
    setVideoForAnalysis(null);
    onStartExternalExtensionAssistant(context);
  };

  const handleSelectCamera = (text: string) => {
    setUserInput((prev) => `${prev} ${text}`.trim());
    setPrompt((prev) => `${prev} ${text}`.trim());
  };

  const handleTranslatePrompt = async (
    promptToTranslate: string,
    setter: (text: string) => void,
  ) => {
    if (!promptToTranslate.trim() || isTranslating) return;
    setIsTranslating(true);
    setError(null);
    try {
      const translated = await toggleTranslateText(promptToTranslate);
      setter(translated);
    } catch (error) {
      console.error('Translation failed:', error);
      setError('Failed to translate the prompt.');
    } finally {
      setIsTranslating(false);
    }
  };

  const handleGenerateStoryboard = async (promptToStoryboard: string) => {
    if (!promptToStoryboard.trim()) return;
    setIsGeneratingStoryboard(true);
    setStoryboardError(null);
    try {
      const result = await generateStoryboard(
        promptToStoryboard,
        activeDogma,
        referenceImages,
        startFrame,
        endFrame,
      );
      setStoryboard(result);
    } catch (err) {
      setStoryboardError(
        err instanceof Error ? err.message : 'Failed to create storyboard.',
      );
    } finally {
      setIsGeneratingStoryboard(false);
    }
  };

  const handleConfirmStoryboard = (finalStoryboard: Storyboard) => {
    const newPrompt = finalStoryboard.keyframes
      .map((kf) => kf.description)
      .join(' ');
    setPrompt(newPrompt);
    setStoryboard(null);
  };

  // Handle video model selection change
  const handleModelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newModelId = e.target.value;
    setSelectedModelId(newModelId);
    // Veo Fast doesn't support 1080p
    if (newModelId === 'veo_fast' && resolution === Resolution.P1080) {
      setResolution(Resolution.P720);
    }
  };

  const handleBananaAction = (targetImage?: ImageFile) => {
    const imgToEdit = targetImage || assistantImage;
    if (imgToEdit) {
      // If image exists, open AI Editor (Banana Edit)
      setEditingImage({
        image: imgToEdit,
        onConfirm: (newImage) => {
          if (targetImage) {
            // If we edited a specific target (like startFrame), update it
            if (targetImage === startFrame) setStartFrame(newImage);
            if (targetImage === endFrame) setEndFrame(newImage);
            // Also update assistant image to keep context
            onAssistantImageChange(newImage);
          } else {
            onAssistantImageChange(newImage);
          }
        }
      });
    } else {
      // If no image, open Image Generator (Banana Gen)
      setIsBananaOpen(true);
    }
  };

  const promptPlaceholder =
    {
      [GenerationMode.TEXT_TO_VIDEO]:
        'A high-stakes handshake between two men...',
      [GenerationMode.FRAMES_TO_VIDEO]: 'Describe the motion between frames...',
      [GenerationMode.REFERENCES_TO_VIDEO]:
        'Describe a video using references...',
      [GenerationMode.EXTEND_VIDEO]: 'Describe what happens next...',

    }[generationMode] || 'Describe the video you want to create...';



  const renderMediaUploads = () => {
    const commonContainerClasses =
      'mb-3 p-4 bg-[#2c2c2e] rounded-xl border border-gray-700 flex flex-col items-center justify-center gap-4';

    const driveModal = isDrivePickerOpen && (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
        <DriveFilePicker
          onSelect={handleDriveFileSelect}
          onCancel={() => setIsDrivePickerOpen(false)}
          mimeTypeFilter="video/*"
        />
      </div>
    );

    // Import Button (Always Visible)
    const importButton = (
      <div className="mb-3 flex gap-2 justify-center">
        {/* Local Upload */}
        <label className="flex items-center gap-2 px-3 py-2 bg-gray-700/50 hover:bg-gray-700 text-gray-300 hover:text-white rounded-xl border border-gray-600 border-dashed cursor-pointer transition-all hover:border-indigo-500">
          <VideoIcon className="w-5 h-5 text-indigo-400" />
          <span className="text-sm font-medium">Importer (Local)</span>
          <input
            type="file"
            accept="video/*"
            ref={externalVideoInputRef}
            onChange={handleExternalVideoFileChange}
            className="hidden"
          />
        </label>
        {/* Drive Upload */}
        <button
          type="button"
          onClick={handleGoogleDriveClick}
          className="flex items-center gap-2 px-3 py-2 bg-gray-700/50 hover:bg-gray-700 text-gray-300 hover:text-white rounded-xl border border-gray-600 border-dashed cursor-pointer transition-all hover:border-green-500"
        >
          <img src="https://upload.wikimedia.org/wikipedia/commons/1/12/Google_Drive_icon_%282020%29.svg" className="w-5 h-5" alt="Drive" />
          <span className="text-sm font-medium">Google Drive</span>
        </button>
      </div>
    );

    let modeContent = null;

    switch (generationMode) {
      case GenerationMode.FRAMES_TO_VIDEO:
        modeContent = (
          <div className={commonContainerClasses}>
            <div className="flex items-center justify-center gap-4">
              <div className="flex flex-col items-center gap-2">
                <ImageUpload
                  label="Start Image"
                  image={startFrame}
                  onSelect={(img) =>
                    handleImageUploadAndDescribe(img, setStartFrame)
                  }
                  onRemove={() => setStartFrame(null)}
                  onEditRequest={(image) => handleBananaAction(image)}
                />
                <button
                  type="button"
                  onClick={() => setImageGenerationTarget('start')}
                  className="text-xs flex items-center gap-1.5 px-2 py-1 bg-gray-600 hover:bg-gray-500 text-white rounded-md transition-colors">
                  <MagicWandIcon className="w-3 h-3" />
                  Generate
                </button>
              </div>
              {!isLooping && (
                <div className="flex flex-col items-center gap-2">
                  <ImageUpload
                    label="End Image"
                    image={endFrame}
                    onSelect={(img) =>
                      handleImageUploadAndDescribe(img, setEndFrame)
                    }
                    onRemove={() => setEndFrame(null)}
                    onEditRequest={(image) => handleBananaAction(image)}
                  />
                  <button
                    type="button"
                    onClick={() => setImageGenerationTarget('end')}
                    className="text-xs flex items-center gap-1.5 px-2 py-1 bg-gray-600 hover:bg-gray-500 text-white rounded-md transition-colors">
                    <MagicWandIcon className="w-3 h-3" />
                    Generate
                  </button>
                </div>
              )}
            </div>
            {startFrame && (
              <div className="mt-3 flex items-center">
                <input
                  id="loop-video-checkbox"
                  type="checkbox"
                  checked={isLooping}
                  onChange={(e) => setIsLooping(e.target.checked)}
                  className="w-4 h-4 text-indigo-600 bg-gray-700 border-gray-600 rounded focus:ring-indigo-500"
                />
                <label
                  htmlFor="loop-video-checkbox"
                  className="ml-2 text-sm font-medium text-gray-300">
                  Create looping video (end frame will be ignored)
                </label>
              </div>
            )}
          </div>
        );
        break;

      case GenerationMode.REFERENCES_TO_VIDEO:
        modeContent = (
          <div className={commonContainerClasses}>
            <button
              type="button"
              onClick={onOpenCharacterManager}
              className="text-sm flex items-center gap-2 px-3 py-1.5 bg-gray-600/80 hover:bg-gray-600 text-white rounded-md transition-colors mb-2">
              <UsersIcon className="w-4 h-4" />
              <span>Load from Character Library</span>
            </button>
            <div className="flex items-center justify-center gap-4">
              {[0, 1, 2].map((index) => (
                <ImageUpload
                  key={index}
                  label={`Reference ${index + 1}`}
                  image={referenceImages[index]}
                  onSelect={(img) => {
                    const newImages = [...referenceImages];
                    newImages[index] = img;
                    setReferenceImages(newImages);
                  }}
                  onRemove={() => {
                    setReferenceImages((prev) =>
                      prev.filter((_, i) => i !== index),
                    );
                  }}
                  onEditRequest={(image) => handleBananaAction(image)}
                />
              ))}
            </div>
          </div>
        );
        break;

      case GenerationMode.EXTEND_VIDEO:
        modeContent = (
          <div className={commonContainerClasses}>
            {inputVideoObject ? (
              <div className="flex flex-col items-center gap-3 w-full">
                <div className="flex items-center gap-2 text-green-400">
                  <FilmIcon className="w-5 h-5" />
                  <span className="text-sm font-medium truncate max-w-[200px]">{inputVideoObject.name}</span>
                </div>
                <div className="text-xs text-gray-500">{(inputVideoObject.size / 1024 / 1024).toFixed(2)} MB</div>

                <button
                  onClick={() => setVideoForAnalysis(inputVideoObject)}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg flex items-center gap-2 text-sm font-medium"
                >
                  <MagicWandIcon className="w-4 h-4" />
                  Analyser pour Extension
                </button>
              </div>
            ) : (
              <div className="text-gray-500 text-sm">Veuillez importer une vidéo ci-dessus.</div>
            )}
          </div>
        );
        break;

      default:
        modeContent = null;
    }

    return (
      <>
        {driveModal}
        {modeContent}
        {generationMode === GenerationMode.EXTEND_VIDEO && importButton}
      </>
    );
  };

  const renderConfigurationForm = () => {
    return (
      <form onSubmit={handleFinalSubmit} className="w-full flex flex-col gap-3">
        <div className="p-4 bg-[#2c2c2e] rounded-xl border border-gray-700" title="Advanced generation settings">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <CustomSelect
              label="Moteur image"
              value={imageProvider || 'nano-pro'}
              onChange={(e) => onImageProviderChange(e.target.value as ImageModelProvider)}
              icon={<PencilIcon className="w-5 h-5 text-gray-400" />}
            >
              <option value="nano-pro">Gemini Nano (Pro)</option>
              {EXPERIMENTAL_TRAINING && (
                <>
                  <option value="flux-2-pro">Flux 2 Pro (Fal)</option>
                  <option value="flux-2-lora">Flux 2 LoRA (Fal)</option>
                  <option value="flux-2-lora-edit">Flux 2 LoRA Edit (Fal)</option>
                  <option value="flux-pro-1.1-ultra">Flux 1.1 Ultra (Fal)</option>
                  <option value="flux-pro-1.1">Flux 1.1 Pro (Fal)</option>
                  <option value="flux-pro-1.0">Flux 1.0 Pro (Fal)</option>
                  <option value="flux-dev">Flux Dev (Fal)</option>
                </>
              )}
            </CustomSelect>

            {/* Video Model Selector */}
            <CustomSelect
              label="Moteur vidéo"
              value={selectedModelId}
              onChange={handleModelChange}
              icon={<VideoIcon className="w-5 h-5 text-gray-400" />}
            >
              {videoModels.map((model) => (
                <option key={model.id} value={model.id}>
                  {model.label}
                </option>
              ))}
            </CustomSelect>

            <CustomSelect
              label="Format"
              value={aspectRatio}
              onChange={(e) => setAspectRatio(e.target.value as AspectRatio)}
              icon={<RectangleStackIcon className="w-5 h-5 text-gray-400" />}
              disabled={generationMode === GenerationMode.EXTEND_VIDEO && !!inputVideoObject}>
              {Object.entries(aspectRatioDisplayNames).map(([key, name]) => (
                <option key={key} value={key}>{name}</option>
              ))}
            </CustomSelect>

            {/* Start Frame Mode - Only shown when reference image exists */}
            {hasReferenceImage && videoSettings && onVideoSettingsChange && (
              <CustomSelect
                label="First Frame"
                value={videoSettings.startFrameMode || 'reference_soft'}
                onChange={(e) => onVideoSettingsChange({
                  ...videoSettings,
                  startFrameMode: e.target.value as StartFrameMode
                })}
                icon={
                  (videoSettings.startFrameMode || 'reference_soft') === 'lock_first_frame'
                    ? <span className="text-green-400">🔒</span>
                    : <span className="text-gray-400">🎬</span>
                }
              >
                {START_FRAME_MODE_OPTIONS.map((mode) => (
                  <option key={mode.id} value={mode.id}>
                    {mode.icon} {mode.label}
                  </option>
                ))}
              </CustomSelect>
            )}
          </div>
        </div>

        {/* Import Video Button (Always Visible) */}
        {renderMediaUploads()}





        <div className="flex flex-col gap-2 bg-[#1f1f1f] border border-gray-600 rounded-2xl p-2 shadow-lg focus-within:ring-2 focus-within:ring-indigo-500">
          <div className="relative flex items-end gap-2">
            <div className="relative" ref={modeSelectorRef}>
              <button
                type="button"
                onClick={() => setIsModeSelectorOpen((prev) => !prev)}
                className="flex shrink-0 items-center gap-2 px-3 py-2.5 rounded-lg hover:bg-gray-700"
                title={`Current mode: ${generationMode}`}>
                {modeIcons[generationMode]}
              </button>
              {isModeSelectorOpen && (
                <div className="absolute bottom-full left-0 mb-2 w-64 bg-[#2c2c2e] border border-gray-700 rounded-lg shadow-xl z-20 p-2">
                  {Object.values(GenerationMode)
                    .filter((m) => m !== GenerationMode.EXTEND_VIDEO)
                    .map((mode) => (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => handleSelectMode(mode)}
                        className={`w-full flex items-center gap-3 p-2 rounded-md text-left text-sm transition-colors ${generationMode === mode
                          ? 'bg-indigo-600 text-white'
                          : 'text-gray-300 hover:bg-gray-700'
                          }`}>
                        {modeIcons[mode]}
                        <span>{mode}</span>
                      </button>
                    ))}
                </div>
              )}
            </div>
            <textarea
              ref={textareaRef}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={promptPlaceholder}
              className="w-full bg-transparent focus:outline-none resize-none text-base text-gray-200 placeholder-gray-500 max-h-48 py-2"
              rows={1}
            />
            <button
              type="submit"
              title="Generate Video"
              className="p-2.5 bg-indigo-600 rounded-full hover:bg-indigo-500">
              <ArrowRightIcon className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>
        <div className="flex flex-col gap-2 mt-2">
          <div className="flex justify-center items-center gap-2 border-t border-gray-700/50 pt-2">
            <button
              type="button"
              onClick={() => handleGenerateStoryboard(prompt)}
              disabled={isGeneratingStoryboard || !prompt.trim()}
              title="Generate Storyboard Preview"
              className="p-2 rounded-full hover:bg-gray-700 text-gray-400 disabled:opacity-50">
              {isGeneratingStoryboard ? (
                <div className="w-5 h-5 border-2 border-t-transparent border-current rounded-full animate-spin"></div>
              ) : (
                <LayoutGridIcon className="w-5 h-5" />
              )}
            </button>
            <button
              type="button"
              onClick={() => handleBananaAction()}
              title="AI Image Tools (Banana)"
              className="p-2 rounded-full hover:bg-gray-700 text-gray-400">
              <PencilIcon className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => handleTranslatePrompt(prompt, setPrompt)}
              disabled={isTranslating || !prompt.trim()}
              title="Translate Prompt (FR/EN)"
              className="p-2 rounded-full hover:bg-gray-700 text-gray-400 disabled:opacity-50">
              {isTranslating ? (
                <div className="w-5 h-5 border-2 border-t-transparent border-current rounded-full animate-spin"></div>
              ) : (
                <SwapHorizontallyIcon className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </form>
    );
  };

  const renderCreativeToolbar = () => {
    return (
      <div className="flex flex-col gap-2 mt-2">
        <div className="flex justify-between items-center border-t border-gray-700 pt-2 px-1">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => handleBananaAction()}
              title={assistantImage ? "Edit Image (Banana)" : "Generate Image (Banana)"}
              className="p-2 rounded-full hover:bg-gray-700 text-gray-400">
              <PencilIcon className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => handleGenerateStoryboard(userInput || prompt)}
              disabled={isGeneratingStoryboard || (!userInput.trim() && !prompt.trim())}
              title="Generate Storyboard Preview"
              className="p-2 rounded-full hover:bg-gray-700 text-gray-400 disabled:opacity-50">
              {isGeneratingStoryboard ? (
                <div className="w-5 h-5 border-2 border-t-transparent border-current rounded-full animate-spin"></div>
              ) : (
                <LayoutGridIcon className="w-5 h-5" />
              )}
            </button>
            <button
              type="button"
              onClick={() => setIsFrameSelectorOpen({ onConfirm: onAssistantImageChange })}
              title="Extract Frame from Video"
              className="p-2 rounded-full hover:bg-gray-700 text-gray-400">
              <ScissorsIcon className="w-5 h-5" />
            </button>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => handleTranslatePrompt(userInput || prompt, userInput ? setUserInput : setPrompt)}
              disabled={isTranslating || (!userInput.trim() && !prompt.trim())}
              title="Translate Prompt (FR/EN)"
              className="p-2 rounded-full hover:bg-gray-700 text-gray-400 disabled:opacity-50">
              {isTranslating ? (
                <div className="w-5 h-5 border-2 border-t-transparent border-current rounded-full animate-spin"></div>
              ) : (
                <SwapHorizontallyIcon className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>

        {/* External Video & Drive Module */}
        <div className="border-t border-gray-700 pt-2 px-1">
          <div className="flex items-center justify-between gap-2 bg-gray-800/30 rounded-lg p-2">
            <span className="text-xs font-medium text-gray-400">Vidéo externe (Extension)</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={async () => {
                  if (!isDriveConnected()) await connectDrive();
                  setIsDrivePickerOpen(true);
                }}
                className={`p-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 transition-colors ${isDriveConnected() ? 'bg-indigo-600/20 text-indigo-300 border border-indigo-500/30 hover:bg-indigo-600/30' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                  }`}
                title="Importer depuis Drive"
              >
                <UploadCloudIcon className="w-3.5 h-3.5" />
                {isDriveConnected() ? 'Drive' : 'Connecter'}
              </button>

              <button
                type="button"
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = 'video/*';
                  input.onchange = async (e) => {
                    const file = (e.target as HTMLInputElement).files?.[0];
                    if (file && onVideoSettingsChange && videoSettings) {
                      // P3 Fix: Upload local file to Supabase to get persistent URL
                      try {
                        const timestamp = Date.now();
                        const path = `temp-import/${user?.id || 'anon'}/${timestamp}_${file.name}`;

                        // 1. Show local preview immediately (Blob)
                        const previewUrl = URL.createObjectURL(file);

                        // Set temporary state while uploading
                        onVideoSettingsChange({
                          ...videoSettings,
                          externalVideo: {
                            source: 'local',
                            fileName: file.name,
                            url: previewUrl, // Deprecated prop
                            previewUrl: previewUrl,
                            sourceUrl: '', // Will be updated after upload
                            file: file
                          }
                        });

                        // 2. Upload to Supabase (P2: External video must have HTTP sourceUrl)
                        const traceId = `upload-${Date.now()}`;
                        console.log('[UPLOAD][START]', { traceId, bucket: 'temp-import', path, fileName: file.name, size: file.size });

                        const { data, error } = await supabase.storage
                          .from('temp-import')
                          .upload(path, file);

                        let finalUrl = '';

                        if (error) {
                          console.warn('[UPLOAD][FALLBACK]', { traceId, error: error.message, fallbackBucket: 'videos' });
                          // Fallback to 'videos' bucket if temp-import fails
                          const { data: fbData, error: fbError } = await supabase.storage
                            .from('videos')
                            .upload(path, file);

                          if (!fbError) {
                            const { data: { publicUrl } } = supabase.storage.from('videos').getPublicUrl(path);
                            finalUrl = publicUrl;
                            console.log('[UPLOAD][SUCCESS]', { traceId, bucket: 'videos', publicUrl });
                          } else {
                            console.error('[UPLOAD][FAILED]', { traceId, error: fbError.message });
                          }
                        } else {
                          const { data: { publicUrl } } = supabase.storage.from('temp-import').getPublicUrl(path);
                          finalUrl = publicUrl;
                          console.log('[UPLOAD][SUCCESS]', { traceId, bucket: 'temp-import', publicUrl });
                        }

                        if (finalUrl) {
                          console.log('[UPLOAD][COMPLETE]', { traceId, sourceUrl: finalUrl, previewUrl: previewUrl.substring(0, 30) + '...' });
                          onVideoSettingsChange({
                            ...videoSettings,
                            externalVideo: {
                              source: 'local',
                              fileName: file.name,
                              url: finalUrl,
                              previewUrl: previewUrl,
                              sourceUrl: finalUrl,
                              file: file
                            }
                          });
                        }

                      } catch (err) {
                        console.error('[PromptAssistant] Local upload error:', err);
                      }
                    }
                  };
                  input.click();
                }}
                className="p-1.5 bg-gray-700 hover:bg-gray-600 rounded-md text-gray-300"
                title="Importer fichier local"
              >
                <FilmIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          {videoSettings?.externalVideo && (
            <div className="mt-2 flex items-center justify-between bg-indigo-900/20 border border-indigo-500/30 rounded px-2 py-1.5">
              <div className="flex items-center gap-2 min-w-0">
                <FilmIcon className="w-3.5 h-3.5 text-indigo-400 shrink-0" />
                <span className="text-xs text-indigo-200 truncate max-w-[140px]">{videoSettings.externalVideo.fileName}</span>
              </div>
              <button type="button" onClick={() => onVideoSettingsChange && onVideoSettingsChange({ ...videoSettings, externalVideo: undefined })} className="text-gray-400 hover:text-white">
                <XMarkIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPreBriefView = () => {
    const renderChips = (category: keyof typeof PREBRIEF_OPTIONS, field: keyof PreBriefData) => (
      <div className="flex flex-wrap gap-2 mt-2">
        {PREBRIEF_OPTIONS[category].map((opt: string) => (
          <button
            key={opt}
            type="button"
            onClick={() => setPreBriefData(prev => ({ ...prev, [field]: opt }))}
            className={`px-3 py-1.5 text-xs rounded-full border transition-all ${preBriefData[field] === opt
              ? 'bg-indigo-600 border-indigo-500 text-white font-bold shadow-md'
              : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-500 hover:text-gray-200'
              }`}
          >
            {opt}
          </button>
        ))}
      </div>
    );

    return (
      <div className="flex flex-col gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-10">
        <div className="bg-gradient-to-r from-indigo-900/40 to-purple-900/40 p-4 rounded-xl border border-indigo-500/20">
          <h3 className="text-sm font-bold text-white mb-1 flex items-center gap-2">
            <SlidersHorizontalIcon className="w-4 h-4 text-indigo-400" />
            Pré-Brief Créatif
          </h3>
          <p className="text-xs text-indigo-200/70">
            Définissez le contexte avant de générer le découpage technique.
          </p>
        </div>

        <div className="space-y-4">
          {/* SUBJECT */}
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 block">Sujet Principal</label>
            <input
              type="text"
              value={preBriefData.subject}
              onChange={(e) => setPreBriefData(prev => ({ ...prev, subject: e.target.value }))}
              placeholder="Décrivez l'action ou le sujet..."
              className="w-full bg-[#1a1a1a] border border-gray-700 rounded-lg p-3 text-sm text-gray-200 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* AMBIANCE */}
            <div className="space-y-4 bg-gray-900/30 p-4 rounded-xl border border-gray-800">
              <h4 className="text-xs font-bold text-indigo-400 uppercase">Ambiance & Lieu</h4>

              <div>
                <label className="text-xs font-medium text-gray-500 block">Lieu / Décor</label>
                {renderChips('location', 'location')}
              </div>

              <div>
                <label className="text-xs font-medium text-gray-500 block">Lumière / Mood</label>
                {renderChips('timeMood', 'timeMood')}
              </div>
            </div>

            {/* REALISATION */}
            <div className="space-y-4 bg-gray-900/30 p-4 rounded-xl border border-gray-800">
              <h4 className="text-xs font-bold text-purple-400 uppercase">Réalisation</h4>

              <div>
                <label className="text-xs font-medium text-gray-500 block">Caméra</label>
                {renderChips('camera', 'camera')}
              </div>

              <div>
                <label className="text-xs font-medium text-gray-500 block">Intention</label>
                {renderChips('intent', 'intent')}
              </div>
            </div>
          </div>

          {/* HYPOTHESIS */}
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 block">Hypothèses & Stratégie (Optionnel)</label>
            <textarea
              value={hypothesis}
              onChange={(e) => setHypothesis(e.target.value)}
              placeholder="E.g. Utilisez des contre-plongées pour accentuer la puissance..."
              className="w-full h-20 bg-[#1a1a1a] border border-gray-700 rounded-lg p-3 text-xs text-gray-300 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
            />
          </div>
        </div>

        {/* ACTIONS - Removed "Propose 3 Options" semi-auto UX */}
        {/* Historical behavior: user confirms context, then clicks "Validate & Compile" */}
        <div className="flex gap-3 pt-4 border-t border-gray-800">
          <button
            onClick={() => {
              // Go back to chat input mode to ask questions
              setAssistantPhase('input');
            }}
            className="flex-1 py-3 text-xs font-bold rounded-lg border transition-all flex items-center justify-center gap-2
              bg-gray-800 hover:bg-gray-700 text-gray-300 border-gray-700"
          >
            ← Retour au Chat
          </button>

          <button
            onClick={() => {
              // Log step transition
              console.log('[PromptSequenceAssistant] Step transition: prebrief → compile');
              executeGeneration();
            }}
            disabled={!preBriefData.subject.trim() || isLoading}
            className={`flex-[2] py-3 text-sm font-bold rounded-lg shadow-lg flex items-center justify-center gap-2 transition-all ${
              preBriefData.subject.trim() && !isLoading
                ? 'bg-green-600 hover:bg-green-500 text-white shadow-green-900/20 hover:scale-[1.02]'
                : 'bg-gray-700 text-gray-500 cursor-not-allowed'
              }`}
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-t-transparent border-white rounded-full animate-spin" />
                Compilation...
              </>
            ) : (
              <>
                <SparklesIcon className="w-4 h-4" />
                Valider & Compiler
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  const handleChainGenerate = async (stepId: string) => {
    const stepIndex = chainSteps.findIndex(s => s.id === stepId);
    if (stepIndex === -1) return;

    const step = chainSteps[stepIndex];
    if (!step.prompt.trim()) {
      setError('Veuillez entrer un prompt.');
      return;
    }

    // Update step loading state
    const updateStep = (update: Partial<ChainStep>) => {
      setChainSteps(prev => prev.map(s => s.id === stepId ? { ...s, ...update } : s));
    };

    updateStep({ isGenerating: true });
    setError(null);

    try {
      // Construct prompt with context
      // If we have a start image, we use it as input for Image-to-Image (Editing/Generation)
      // If no start image, Text-to-Image

      let resultImage: ImageFile;
      // PIVOT: Use Nano Pro directly for fast high-quality generation

      if (step.startImage) {
        // Image-to-Image / Edit logic
        // Ideally we want to generate a NEW image that follows the prompt, using startImage as strong reference
        // Currently using 'editImage' which effectively does this with Nano
        const edited = await editImage(
          [step.startImage],
          step.prompt + " (Next frame in sequence, visual continuity, cinematic)",
          activeDogma,
          'gemini-2.5-flash-image', // Fast model for chain
          apiKey || undefined,
          characters
        );
        resultImage = edited;
      } else {
        // Text-to-Image
        const generated = await generateImageFromText(
          step.prompt + " (Cinematic shot)",
          activeDogma
        );
        resultImage = generated;
      }

      updateStep({ endImage: resultImage, isGenerating: false });
    } catch (err: any) {
      console.error('Chain Gen Error:', err);
      setError(err.message || 'Generation failed');
      updateStep({ isGenerating: false });
    }
  };

  const handleChainContinue = (stepId: string) => {
    const stepIndex = chainSteps.findIndex(s => s.id === stepId);
    if (stepIndex === -1) return;
    const currentStep = chainSteps[stepIndex];

    if (!currentStep.endImage) return;

    // Add new step
    const newStep: ChainStep = {
      id: Date.now().toString(),
      startImage: currentStep.endImage, // Pass end image as start for next
      prompt: '',
      endImage: null,
      isGenerating: false
    };

    setChainSteps(prev => [...prev, newStep]);
  };

  const renderInfiniteChainView = () => {
    return (
      <div className="flex flex-col gap-8 pb-20">
        <div className="bg-gradient-to-r from-blue-900/40 to-cyan-900/40 p-4 rounded-xl border border-blue-500/20">
          <h3 className="text-sm font-bold text-white mb-1 flex items-center gap-2">
            <span className="text-xl">🔗</span>
            Chaînage Infini (Keyframe Flow)
          </h3>
          <p className="text-xs text-blue-200/70">
            Générez une séquence image par image. L'image de fin devient le départ de la suivante.
          </p>
        </div>

        <div className="space-y-12 relative before:absolute before:left-8 before:top-4 before:bottom-0 before:w-0.5 before:bg-gradient-to-b before:from-indigo-500 before:to-transparent before:opacity-30">
          {chainSteps.map((step, index) => (
            <div key={step.id} className="relative pl-20 animate-in fade-in slide-in-from-bottom-4 duration-500">
              {/* Step Number Badge */}
              <div className="absolute left-6 top-1/2 -translate-y-1/2 w-5 h-5 bg-indigo-900 border border-indigo-500 rounded-full flex items-center justify-center text-[10px] font-bold text-indigo-300 z-10">
                {index + 1}
              </div>

              <div className="flex items-center gap-4 bg-[#1a1a1a] p-4 rounded-xl border border-gray-800 shadow-xl overflow-x-auto">

                {/* START IMAGE */}
                <div className="flex-shrink-0 flex flex-col gap-2 w-48">
                  <span className="text-[10px] uppercase font-bold text-gray-500 tracking-wider">Départ</span>
                  <div className="aspect-video bg-gray-900 rounded-lg border border-gray-700 relative group overflow-hidden">
                    {step.startImage ? (
                      <>
                        <img src={URL.createObjectURL(step.startImage.file)} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <button
                            onClick={() => setIsFrameSelectorOpen({
                              onConfirm: (img) => setChainSteps(prev => prev.map(s => s.id === step.id ? { ...s, startImage: img } : s))
                            })}
                            className="p-1.5 bg-gray-800 rounded-full text-white hover:bg-gray-700" title="Remplacer via Vidéo">
                            <ScissorsIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </>
                    ) : (
                      <button
                        onClick={() => {
                          // Trigger file upload or choice
                          // Simple hack: toggle frame selector for now or just click "generate" for text-to-image
                          setIsFrameSelectorOpen({
                            onConfirm: (img) => setChainSteps(prev => prev.map(s => s.id === step.id ? { ...s, startImage: img } : s))
                          });
                        }}
                        className="w-full h-full flex flex-col items-center justify-center text-gray-600 hover:text-gray-400 hover:bg-gray-800/50 transition-colors"
                      >
                        <UploadCloudIcon className="w-6 h-6 mb-1" />
                        <span className="text-[9px]">Ajouter Image</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* ARROW & PROMPT */}
                <div className="flex-grow flex flex-col gap-2 min-w-[200px]">
                  <div className="flex items-center gap-2 text-gray-600 mb-1">
                    <div className="h-0.5 flex-grow bg-gray-700"></div>
                    <ArrowRightIcon className="w-4 h-4" />
                    <div className="h-0.5 flex-grow bg-gray-700"></div>
                  </div>
                  <textarea
                    value={step.prompt}
                    onChange={(e) => setChainSteps(prev => prev.map(s => s.id === step.id ? { ...s, prompt: e.target.value } : s))}
                    placeholder="Décrivez l'évolution vers l'image de fin..."
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg p-2 text-xs text-gray-300 resize-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 h-20"
                  />
                  <button
                    onClick={() => handleChainGenerate(step.id)}
                    disabled={step.isGenerating || !step.prompt.trim()}
                    className="mt-auto w-full py-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-700 text-white text-xs font-bold rounded-lg flex items-center justify-center gap-2"
                  >
                    {step.isGenerating ? (
                      <div className="w-3 h-3 border-2 border-t-transparent border-white rounded-full animate-spin" />
                    ) : (
                      <SparklesIcon className="w-3 h-3" />
                    )}
                    {step.endImage ? 'Régénérer' : 'Générer Fin'}
                  </button>
                </div>

                {/* END IMAGE */}
                <div className="flex-shrink-0 flex flex-col gap-2 w-48">
                  <span className="text-[10px] uppercase font-bold text-gray-500 tracking-wider">Arrivée</span>
                  <div className="aspect-video bg-gray-900 rounded-lg border border-gray-700 relative overflow-hidden flex items-center justify-center">
                    {step.endImage ? (
                      <img src={URL.createObjectURL(step.endImage.file)} className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-gray-700 text-[9px] text-center px-4">
                        En attente...
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ACTION: CONTINUE */}
              {step.endImage && index === chainSteps.length - 1 && (
                <div className="mt-4 flex justify-center">
                  <button
                    onClick={() => handleChainContinue(step.id)}
                    className="px-6 py-2 bg-green-600 hover:bg-green-500 text-white text-xs font-bold rounded-full shadow-lg shadow-green-900/20 flex items-center gap-2 animate-bounce"
                  >
                    Continuer la séquence <ArrowRightIcon className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
          ))}
          <div ref={chainEndRef} />
        </div>
      </div>
    );
  };

  const renderProposalView = () => {
    return (
      <div className="flex flex-col gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-10">
        <div className="bg-gradient-to-r from-purple-900/40 to-pink-900/40 p-4 rounded-xl border border-purple-500/20">
          <h3 className="text-sm font-bold text-white mb-1 flex items-center gap-2">
            <LayoutGridIcon className="w-4 h-4 text-purple-400" />
            Propositions Créatives
          </h3>
          <p className="text-xs text-purple-200/70">
            Choisissez une approche ou affinez votre vision avant de générer.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4">
          {proposals.map((proposal) => (
            <button
              key={proposal.id}
              onClick={() => setSelectedProposalId(proposal.id)}
              className={`text-left p-4 rounded-xl border transition-all ${selectedProposalId === proposal.id
                ? 'bg-indigo-600/20 border-indigo-500 shadow-lg shadow-indigo-900/20'
                : 'bg-gray-900/50 border-gray-700 hover:border-gray-500 hover:bg-gray-800/50'
                }`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-lg ${selectedProposalId === proposal.id ? 'bg-indigo-600' : 'bg-gray-700'
                  }`}>
                  {proposal.id.includes('1') ? '🎬' : proposal.id.includes('2') ? '🎨' : '⚡'}
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-white mb-1">{proposal.title}</h4>
                  <p className="text-xs text-gray-400 mb-2">{proposal.description}</p>
                  <p className="text-[10px] text-indigo-300/70 italic">💡 {proposal.reasoning}</p>
                </div>
                {selectedProposalId === proposal.id && (
                  <div className="w-5 h-5 rounded-full bg-indigo-500 flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>

        <div className="flex gap-3 pt-4 border-t border-gray-800">
          <button
            onClick={() => setAssistantPhase('prebrief')}
            className="flex-1 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs font-bold rounded-lg border border-gray-700 transition-all"
          >
            ← Retour au Brief
          </button>

          <button
            onClick={() => {
              // Inject selected proposal into hypothesis
              const selected = proposals.find(p => p.id === selectedProposalId);
              if (selected) {
                setHypothesis(prev => `${prev}\n\nApproche choisie: ${selected.title}\n${selected.description}`);
              }
              executeGeneration();
            }}
            disabled={!selectedProposalId}
            className={`flex-[2] py-3 text-sm font-bold rounded-lg shadow-lg flex items-center justify-center gap-2 transition-all ${selectedProposalId
              ? 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-indigo-900/20 hover:scale-[1.02]'
              : 'bg-gray-700 text-gray-500 cursor-not-allowed'
              }`}
          >
            <SparklesIcon className="w-4 h-4" />
            Générer avec cette approche
          </button>
        </div>
      </div>
    );
  };

  const renderChatForm = () => {
    return (
      <div className="mt-auto flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-400">Active Dogma:</span>
            {activeDogma ? (
              <span className="text-sm font-semibold px-2 py-1 rounded-md bg-green-500/10 text-green-300 border border-green-500/30">
                {activeDogma.title}
              </span>
            ) : (
              <span className="text-sm font-semibold px-2 py-1 rounded-md bg-red-500/10 text-red-300 border border-red-500/30">
                None
              </span>
            )}
            {projectName && (
              <span className="text-sm font-semibold px-2 py-1 rounded-md bg-indigo-500/10 text-indigo-300 border border-indigo-500/30 ml-2">
                📂 Projet : {projectName}
              </span>
            )}
          </div>
          <button
            onClick={onOpenDogmaManager}
            className="text-sm text-indigo-400 hover:underline">
            Manage
          </button>
        </div>

        {!extensionContext && (
          <div className="flex items-center gap-4 mb-3 flex-wrap">
            <div className="flex items-center gap-2">
              <label htmlFor="duration-input" className="text-sm font-medium text-gray-400">Durée</label>
              <input
                id="duration-input"
                type="number"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                min="8"
                max="60"
                className="w-16 bg-[#1f1f1f] border border-gray-600 rounded-lg p-2 text-center text-sm"
              />
              <span className="text-xs text-gray-500">sec</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-400">Mode:</span>
              <div className="relative">
                <select
                  value={sequenceMode}
                  onChange={(e) => onSequenceModeChange?.(e.target.value as 'plan-sequence' | 'decoupage' | 'infinite-chain')}
                  className="bg-gray-900 border border-gray-700 text-xs rounded-lg focus:ring-indigo-500 focus:border-indigo-500 block w-full p-1.5 text-white"
                >
                  <option value="plan-sequence">⚡ Plan Extensif (Génération Rapide)</option>
                  <option value="decoupage">🎞️ Découpage (Séquence Précise)</option>
                  <option value="infinite-chain">🔗 Chaînage Infini (Start/End)</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {assistantImage && (
          <div className="p-1 mb-2 bg-gray-900/50 rounded-lg relative w-24">
            <img src={URL.createObjectURL(assistantImage.file)} alt="Preview" className="w-full h-auto rounded" />
            <button
              onClick={() => onAssistantImageChange(null)}
              className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 hover:bg-red-500 rounded-full flex items-center justify-center text-white shadow-sm border border-red-700"
            >
              <XMarkIcon className="w-3 h-3" />
            </button>
          </div>
        )}

        {detectedChars.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2 px-1 animate-in fade-in slide-in-from-bottom-2">
            <span className="text-xs font-medium text-gray-500 self-center">Active Cast:</span>
            {detectedChars.map(c => (
              <span key={c.id} className="flex items-center gap-1 bg-indigo-500/20 text-indigo-200 px-2.5 py-1 rounded-full text-xs font-bold border border-indigo-500/40 shadow-sm">
                <UsersIcon className="w-3.5 h-3.5" />
                {c.name}
              </span>
            ))}
          </div>
        )}

        <form onSubmit={handleChatSubmit} className="flex items-start gap-2 bg-[#1f1f1f] border border-gray-600 rounded-lg p-2 shadow-sm focus-within:ring-2 focus-within:ring-indigo-500">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="p-2.5 rounded-full hover:bg-gray-700 text-gray-300">
            <PaperclipIcon className="w-5 h-5" />
          </button>
          <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/*" className="hidden" />

          <div className="flex-grow relative">
            <textarea
              ref={chatTextareaRef}
              value={userInput}
              onChange={(e) => {
                const val = e.target.value;
                setUserInput(val);
                const lastAtIndex = val.lastIndexOf('@');
                if (lastAtIndex !== -1 && lastAtIndex >= val.length - 20) {
                  setMentionFilter(val.substring(lastAtIndex + 1).split(/\s/)[0]);
                  setShowMentions(true);
                  setSelectedMentionIndex(0);
                } else {
                  setShowMentions(false);
                }
              }}
              onKeyDown={(e) => {
                if (showMentions) {
                  if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedMentionIndex(prev => (prev + 1) % filteredCharacters.length); }
                  else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedMentionIndex(prev => (prev - 1 + filteredCharacters.length) % filteredCharacters.length); }
                  else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); if (filteredCharacters[selectedMentionIndex]) handleInsertMention(filteredCharacters[selectedMentionIndex]); }
                  else if (e.key === 'Escape') setShowMentions(false);
                } else if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleChatSubmit(e);
                }
              }}
              placeholder="Describe your scene here, use @ to mention a character..."
              className="w-full bg-transparent focus:outline-none text-base text-gray-200 placeholder-gray-500 px-2 resize-none leading-tight py-2.5"
              disabled={isLoading || !!finalResult}
              rows={1}
            />

            {showMentions && filteredCharacters.length > 0 && (
              <div className="absolute bottom-full left-0 mb-2 w-64 bg-[#2c2c2e] border border-gray-700 rounded-xl shadow-2xl z-50 overflow-hidden">
                <div className="p-2 border-b border-gray-700 bg-gray-800/50">
                  <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Membres du Cast</span>
                </div>
                <div className="max-h-60 overflow-y-auto scrollbar-hide">
                  {filteredCharacters.map((char, idx) => (
                    <button
                      key={char.id}
                      type="button"
                      onClick={() => handleInsertMention(char)}
                      className={`w-full flex items-center gap-3 p-3 text-left ${idx === selectedMentionIndex ? 'bg-indigo-600' : 'hover:bg-gray-700'}`}
                    >
                      <div className="w-8 h-8 rounded-full bg-indigo-900/50 overflow-hidden shrink-0 border border-white/10">
                        {char.images?.[0] ? <img src={`data:${char.images[0].type};base64,${char.images[0].base64}`} className="w-full h-full object-cover" /> : <UsersIcon className="w-4 h-4 text-indigo-300 mx-auto mt-2" />}
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm font-bold text-white truncate">{char.name}</span>
                        <span className="text-[10px] text-gray-400 truncate">{char.description}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={!isAuthReady || isLoading || !!finalResult || (!userInput.trim() && !assistantImage)}
            title={!isAuthReady ? 'Connectez-vous pour continuer' : undefined}
            className="p-2.5 bg-indigo-600 rounded-md hover:bg-indigo-500 disabled:bg-gray-600 disabled:cursor-not-allowed">
            <ArrowRightIcon className="w-5 h-5 text-white" />
          </button>
        </form>
        {renderCreativeToolbar()}
      </div>
    );
  };

  return (
    <div className="bg-[#1f1f1f] border border-gray-700 rounded-2xl h-full flex flex-col shadow-lg overflow-hidden">
      {/* Modals */}
      {editingImage && (
        <AIEditorModal
          image={editingImage.image}
          onClose={() => setEditingImage(null)}
          onConfirm={(newImage) => {
            editingImage.onConfirm(newImage);
            setEditingImage(null);
          }}
          dogma={activeDogma}
        />
      )}
      {storyboard && startFrame && (
        <StoryboardPreviewModal
          isOpen={!!storyboard}
          onClose={() => setStoryboard(null)}
          onApplyVariant={(payload) => {
            if (payload.previewImage) onAssistantImageChange(payload.previewImage);
            if (payload.previewPrompt) setPrompt(payload.previewPrompt);
            setStoryboard(null);
          }}
          segmentIndex={0}
          baseImage={startFrame}
          currentPrompt={prompt}
          dogma={activeDogma}
          mode="single-select"
        />
      )}
      {isFrameSelectorOpen && (
        <VideoFrameSelectorModal
          isOpen={!!isFrameSelectorOpen}
          onClose={() => setIsFrameSelectorOpen(null)}
          onConfirm={isFrameSelectorOpen.onConfirm}
        />
      )}
      {imageGenerationTarget && (
        <ImageGenerationModal
          isOpen={!!imageGenerationTarget}
          onClose={() => setImageGenerationTarget(null)}
          onConfirm={(img) => {
            if (imageGenerationTarget === 'start') setStartFrame(img);
            if (imageGenerationTarget === 'end') setEndFrame(img);
            setImageGenerationTarget(null);
          }}
          generateImageFn={(p) => generateImageFromText(p, activeDogma)}
        />
      )}
      {isBananaOpen && (
        <ImageGenerationModal
          isOpen={isBananaOpen}
          initialPrompt={pendingDecoupagePrompt || undefined}
          onClose={() => setIsBananaOpen(false)}
          onConfirm={(img) => {
            if (pendingDecoupagePrompt) {
              handleMasterShotConfirmed(img);
            } else {
              onAssistantImageChange(img);
              setIsBananaOpen(false);
            }
          }}
          generateImageFn={(p) => generateImageFromText(p, activeDogma)}
        />
      )}
      {videoForAnalysis && (
        <VideoAnalysisModal
          videoFile={videoForAnalysis}
          onClose={() => setVideoForAnalysis(null)}
          onConfirm={handleAnalysisConfirm}
        />
      )}
      {isDrivePickerOpen && (
        <DriveFilePicker
          onCancel={() => setIsDrivePickerOpen(false)}
          onSelect={async (file: any) => {
            // [FAL PROOF MODE]
            // We need a direct, playable URL for LTX/Video. 
            // webViewLink is just a viewer. webContentLink might work but often 403.
            // Best Proof: Proxy via backend to get a signed Supabase URL.

            let finalUrl = file.webViewLink || file.webContentLink || '';

            if (user?.id) {
              try {
                const session = await supabase.auth.getSession();
                const token = session.data.session?.access_token;

                console.log('[PromptAssistant] Requesting Direct Drive Proxy URL...', file.id);
                const res = await fetch('/api/google/drive/proxy-url', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify({ fileId: file.id })
                });

                if (res.ok) {
                  const data = await res.json();
                  if (data.url) {
                    console.log('[PromptAssistant] Proxy Success:', data.url);
                    finalUrl = data.url;
                  }
                } else {
                  console.warn('[PromptAssistant] Proxy failed:', res.status);
                }
              } catch (e) {
                console.error('[PromptAssistant] Proxy error:', e);
              }
            }

            if (onVideoSettingsChange && videoSettings) {
              onVideoSettingsChange({
                ...videoSettings,
                externalVideo: {
                  source: 'gdrive',
                  fileName: file.name,
                  url: finalUrl,

                  // P3 Fix: Provide explicit previewUrl for UI (same as source for Drive)
                  previewUrl: finalUrl,
                  sourceUrl: finalUrl,

                  file: null
                }
              });
            }
            setIsDrivePickerOpen(false);
          }}
          mimeTypeFilter="video/mp4"
        />
      )}
      {/* Header */}
      <header className="flex border-b border-gray-700 p-3 justify-between items-center bg-[#252528] flex-shrink-0">
        <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
          {extensionContext || assistantReferenceVideo ? 'Extension' : 'Assistant Concept'}
        </h3>
        <div className="flex items-center gap-3">
          {(extensionContext || assistantReferenceVideo || initialValues) && (
            <button onClick={onClearContext} className="text-xs text-indigo-400 hover:underline">Reset</button>
          )}
          {onToggleConfigMode && (
            <button
              onClick={onToggleConfigMode}
              className={`p-1.5 rounded-lg transition-all border ${forceConfigMode ? 'bg-indigo-600 border-indigo-500 text-white shadow-lg' : 'bg-transparent border-transparent text-gray-400 hover:text-white hover:bg-gray-800'}`}
              title="Toggle Layout"
            >
              <LayoutGridIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </header>

      {/* COMPACT MODEL TOOLBAR */}
      <div className="flex flex-wrap items-center gap-3 p-3 bg-[#1a1a1a] border-b border-[#3f3f46] flex-shrink-0">
        <CustomSelect
          label="Image"
          value={imageProvider}
          onChange={(e) => onImageProviderChange(e.target.value as ImageModelProvider)}
          icon={<MagicWandIcon className="w-3.5 h-3.5 text-purple-400" />}
        >
          <option value="nano-pro">Nano Pro</option>
          <option value="nano-fal-edit">Nano Pro Edit</option>
          <option value="flux-2-pro">Flux 2 Pro</option>
          <option value="flux-2-lora">Flux 2 LoRA</option>
          <option value="flux-2-lora-edit">Flux 2 LoRA Edit</option>
          <option value="flux-pro-1.1-ultra">Flux 1.1 Ultra</option>
          <option value="flux-pro-1.1">Flux 1.1 Pro</option>
          <option value="flux-pro-1.0">Flux 1.0 Pro</option>
          <option value="flux-dev">Flux Dev</option>
        </CustomSelect>

        {/* Video Selector Removed from Toolbar - Handled in Studio Config */}
        {/* Video Selector Removed from Toolbar - Handled in Studio Config */}

        <div className="h-6 w-px bg-gray-700 mx-1" />

        <div className="flex items-center gap-1">
          <button
            onClick={() => setGenerationMode(GenerationMode.TEXT_TO_VIDEO)}
            aria-label="Text to Video"
            title="Mode Texte vers Vidéo : Générer une vidéo à partir d'une description textuelle"
            className={`flex items-center gap-1.5 px-2 py-1 rounded text-[10px] font-medium transition-all ${generationMode === GenerationMode.TEXT_TO_VIDEO
              ? 'bg-indigo-600 text-white shadow-sm'
              : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}
          >
            <TextModeIcon className="w-3 h-3" />
            <span>Texte</span>
          </button>
          <button
            onClick={() => setGenerationMode(GenerationMode.FRAMES_TO_VIDEO)}
            aria-label="Frame to Video"
            title="Mode Image vers Vidéo : Animer une image existante"
            className={`flex items-center gap-1.5 px-2 py-1 rounded text-[10px] font-medium transition-all ${generationMode === GenerationMode.FRAMES_TO_VIDEO
              ? 'bg-indigo-600 text-white shadow-sm'
              : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}
          >
            <FramesModeIcon className="w-3 h-3" />
            <span>Image</span>
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-grow p-4 overflow-y-auto scrollbar-hide">
        {referenceVideoUrl && (
          <div className="mb-4 p-2 bg-gray-900/50 rounded-xl border border-gray-700 overflow-hidden">
            <video src={referenceVideoUrl} autoPlay loop muted playsInline className="w-full rounded-lg aspect-video object-cover" />
          </div>
        )}
        {assistantPhase === 'prebrief' ? (
          renderPreBriefView()
        ) : assistantPhase === 'proposal' ? (
          renderProposalView()
        ) : (
          /* Note: infinite-chain mode now uses InfiniteChainPanel in right panel */
          <div className="space-y-4">
            {messages.map((msg, index) => (
              <div key={index} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] p-3 rounded-2xl flex flex-col gap-2 ${msg.role === 'user' ? 'bg-indigo-600 text-white rounded-br-none shadow-md' : 'bg-[#2c2c2e] text-gray-200 rounded-bl-none border border-gray-700 shadow-sm'}`}>
                  {msg.image && <img src={URL.createObjectURL(msg.image.file)} alt="Context" className="rounded-lg max-w-full border border-white/10" />}
                  {msg.content && <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>}
                </div>
              </div>
            ))}
            {isLoading && !finalResult && (
              <div className="flex justify-start">
                <div className="p-3 bg-[#2c2c2e] rounded-2xl flex items-center gap-2 border border-gray-700 shadow-sm">
                  <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                  <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                  <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce"></div>
                </div>
              </div>
            )}
            {finalResult && (
              <div className="bg-indigo-900/10 p-4 rounded-xl border border-indigo-500/30 space-y-4 animate-in fade-in slide-in-from-bottom-4 shadow-inner">
                <div className="space-y-3">
                  <div>
                    <h4 className="text-[10px] font-bold uppercase text-indigo-400 tracking-wider mb-1">Creative Brief</h4>
                    <p className="text-sm text-gray-300 bg-black/20 p-3 rounded-lg border border-white/5">{finalResult.creativePrompt}</p>
                  </div>
                  <div>
                    <h4 className="text-[10px] font-bold uppercase text-green-400 tracking-wider mb-1">VEO Optimized Prompt</h4>
                    <p className="text-sm text-indigo-200 bg-black/20 p-3 rounded-lg border border-white/5 italic">{finalResult.veoOptimizedPrompt}</p>
                  </div>
                </div>
                <div className="text-center text-[10px] font-bold text-green-400 bg-green-400/10 py-1.5 rounded-full border border-green-400/20">
                  ✓ Séquence générée avec succès
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}

      </div >

      {/* Footer Area */}
      {
        assistantPhase !== 'prebrief' && assistantPhase !== 'proposal' && (
          <div className="p-4 bg-[#252528] border-t border-gray-700 flex-shrink-0">
            {(forceConfigMode || !!finalResult) ? renderConfigurationForm() : renderChatForm()}
            {error && <p className="text-[11px] text-red-400 mt-2 text-center bg-red-400/5 py-1 rounded border border-red-400/10">⚠️ {error}</p>}
          </div>
        )
      }
    </div >
  );
};

export default PromptSequenceAssistant;